import { useEffect, useMemo, useState } from "react";
import { api, fmtErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { DownloadSimple, Lightning, Users, Stack, CurrencyDollar } from "@phosphor-icons/react";
import {
  Bar, BarChart, CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import { toast } from "sonner";

const usd = (v) => (v == null ? "—" : `$${Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}`);

const Meter = ({ icon: Icon, label, used, limit, testid }) => {
  const pct = limit ? Math.min(100, Math.round((used / limit) * 100)) : 0;
  const tone = pct >= 100 ? "var(--c-red)" : pct >= 80 ? "var(--c-amber)" : "var(--c-accent)";
  return (
    <div className="rounded-lg border border-[var(--c-border)] bg-[var(--c-surface)] p-4" data-testid={testid}>
      <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-[var(--c-muted)]">
        <Icon size={13} /> {label}
      </div>
      <div className="mt-2 font-mono text-xl text-[var(--c-text)]">
        {used}<span className="text-[var(--c-muted)] text-sm"> / {limit}</span>
      </div>
      <div className="mt-2.5 h-1 rounded-full bg-[var(--c-border)] overflow-hidden">
        <div className="h-full transition-all duration-500" style={{ width: `${pct}%`, background: tone }} />
      </div>
      <div className="mt-1.5 font-mono text-[10px]" style={{ color: tone }}>{pct}% used</div>
    </div>
  );
};

export default function Pricing() {
  const { user } = useAuth();
  const [lic, setLic] = useState(null);
  const [tiers, setTiers] = useState([]);
  const [usage, setUsage] = useState([]);
  const [sim, setSim] = useState(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    volumes: "10, 25, 50, 100, 250", seats: 20, tier: "professional",
    engineer_cost_usd_per_hour: 45, manual_hours_per_project: 120,
    automated_hours_per_project: 6, use_actuals: true,
  });
  const [plan, setPlan] = useState({ tier: "professional", seats: 0, projects_per_month: 0, ai_ops_per_month: 0, enforced: true });

  const load = async () => {
    try {
      const [l, t, u] = await Promise.all([api.get("/license"), api.get("/license/tiers"), api.get("/license/usage")]);
      setLic(l.data);
      setTiers(t.data.tiers);
      setUsage(u.data.series);
      setPlan((p) => ({ ...p, tier: l.data.license.tier, seats: l.data.license.seats,
        projects_per_month: l.data.license.projects_per_month, ai_ops_per_month: l.data.license.ai_ops_per_month,
        enforced: l.data.license.enforced }));
      setForm((f) => ({ ...f, tier: l.data.license.tier, seats: l.data.usage.seats_used || f.seats }));
    } catch (e) { toast.error(fmtErr(e)); }
  };
  useEffect(() => { load(); }, []);

  const payload = useMemo(() => ({
    ...form,
    volumes: form.volumes.split(",").map((v) => parseInt(v, 10)).filter((v) => v > 0),
    seats: Number(form.seats) || 1,
  }), [form]);

  const runSim = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/pricing/simulate", payload);
      setSim(data);
    } catch (e) { toast.error(fmtErr(e)); } finally { setBusy(false); }
  };
  useEffect(() => { runSim(); /* eslint-disable-next-line */ }, []);

  const download = async () => {
    setBusy(true);
    try {
      const res = await api.post("/pricing/worksheet", payload, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "formigen_pricing_worksheet.xlsx"; a.click();
      URL.revokeObjectURL(url);
      toast.success("Pricing worksheet downloaded");
    } catch (e) { toast.error(fmtErr(e)); } finally { setBusy(false); }
  };

  const savePlan = async () => {
    setBusy(true);
    try {
      await api.put("/license", { ...plan, org_id: lic?.org_id });
      toast.success("Plan updated");
      load();
    } catch (e) { toast.error(fmtErr(e)); } finally { setBusy(false); }
  };

  const axis = { stroke: "var(--c-muted)", fontSize: 10, fontFamily: "JetBrains Mono" };
  const tip = { background: "var(--c-surface)", border: "1px solid var(--c-border)", borderRadius: 8, fontSize: 11 };

  return (
    <div className="p-6 max-w-7xl" data-testid="pricing-page">
      <h2 className="text-2xl font-semibold tracking-tight text-[var(--c-text)] font-heading">Plan, Usage & Pricing</h2>
      <p className="text-xs text-[var(--c-muted)] mt-1.5 mb-6 max-w-2xl">
        Seats and monthly volume are enforced in-app. The simulator prices AI cost per project from your metered
        usage and models the gross margin of each plan.
      </p>

      {lic && (
        <>
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <span className="inline-flex items-center gap-2 rounded-full border border-[var(--c-accent)]/40 bg-[var(--c-accent)]/10 px-3 py-1 text-xs text-[var(--c-accent)]" data-testid="plan-badge">
              <Lightning size={12} weight="fill" /> {lic.license.tier_name} plan
            </span>
            <span className="text-[11px] text-[var(--c-muted)]">{lic.license.description}</span>
            {!lic.license.enforced && <span className="text-[11px] text-[var(--c-amber)]">Limits not enforced</span>}
            <span className="ml-auto font-mono text-[11px] text-[var(--c-muted)]">
              AI spend this month: <span className="text-[var(--c-text)]">${lic.usage.ai_cost_month_usd}</span>
            </span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="usage-meters">
            <Meter icon={Users} label="Seats" used={lic.usage.seats_used} limit={lic.usage.seats_limit} testid="meter-seats" />
            <Meter icon={Stack} label="Projects this month" used={lic.usage.projects_month} limit={lic.usage.projects_limit} testid="meter-projects" />
            <Meter icon={Lightning} label="AI operations" used={lic.usage.ai_ops_month} limit={lic.usage.ai_ops_limit} testid="meter-ai" />
          </div>
        </>
      )}

      {usage.length > 0 && (
        <div className="mt-5 rounded-lg border border-[var(--c-border)] bg-[var(--c-surface)] p-4" data-testid="usage-chart">
          <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--c-muted)] mb-3">Metered AI cost by month</div>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={usage}>
                <CartesianGrid stroke="var(--c-border)" strokeDasharray="3 3" />
                <XAxis dataKey="period" {...axis} />
                <YAxis {...axis} />
                <Tooltip contentStyle={tip} />
                <Line type="monotone" dataKey="cost_usd" name="AI cost (USD)" stroke="var(--c-accent)" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="ops" name="Operations" stroke="var(--c-ember)" strokeWidth={1.5} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="rounded-lg border border-[var(--c-border)] bg-[var(--c-surface)] p-4 space-y-3" data-testid="sim-inputs">
          <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--c-muted)]">Simulator inputs</div>
          {[["volumes", "Project volumes / month"], ["seats", "Billed seats"],
            ["engineer_cost_usd_per_hour", "Engineer cost (USD/h)"],
            ["manual_hours_per_project", "Manual hours / project"],
            ["automated_hours_per_project", "Automated hours / project"]].map(([k, label]) => (
            <div key={k} className="space-y-1.5">
              <Label className="text-[var(--c-muted)] text-xs">{label}</Label>
              <Input data-testid={`sim-${k}`} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                className="bg-[var(--c-bg)] border-[var(--c-border)] text-[var(--c-text)] font-mono h-8 text-xs" />
            </div>
          ))}
          <div className="space-y-1.5">
            <Label className="text-[var(--c-muted)] text-xs">Plan modelled</Label>
            <Select value={form.tier} onValueChange={(v) => setForm({ ...form, tier: v })}>
              <SelectTrigger data-testid="sim-tier" className="bg-[var(--c-bg)] border-[var(--c-border)] text-[var(--c-text)] h-8 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[var(--c-surface)] border-[var(--c-border)]">
                {tiers.map((t) => <SelectItem key={t.key} value={t.key} className="text-[var(--c-text)]">{t.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center justify-between pt-1">
            <span className="text-xs text-[var(--c-muted)]">Use metered actuals</span>
            <Switch data-testid="sim-actuals" checked={form.use_actuals} onCheckedChange={(v) => setForm({ ...form, use_actuals: v })} />
          </div>
          <div className="flex gap-2 pt-1">
            <Button size="sm" data-testid="sim-run" onClick={runSim} disabled={busy}
              className="bg-[var(--c-accent)] hover:bg-[var(--c-accent-hover)] text-white rounded-sm h-8 text-xs flex-1">Simulate</Button>
            <Button size="sm" variant="outline" data-testid="worksheet-download" onClick={download} disabled={busy}
              className="border-[var(--c-border)] text-[var(--c-muted)] hover:text-[var(--c-text)] hover:bg-[var(--c-surface-2)] rounded-sm h-8 text-xs">
              <DownloadSimple size={13} className="mr-1" /> Excel
            </Button>
          </div>
          {sim && (
            <div className="pt-2 border-t border-[var(--c-border)] font-mono text-[10px] text-[var(--c-muted)] leading-relaxed">
              AI cost / project <span className="text-[var(--c-text)]">${sim.assumptions.ai_cost_per_project_usd}</span>
              {" "}({sim.assumptions.source}
              {sim.assumptions.metered_projects ? `, ${sim.assumptions.metered_projects} projects metered` : ""})
            </div>
          )}
        </div>

        <div className="lg:col-span-2 space-y-4">
          {sim && (
            <div className="rounded-lg border border-[var(--c-border)] bg-[var(--c-surface)] p-4" data-testid="sim-chart">
              <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--c-muted)] mb-3">
                AI cost vs revenue by volume
              </div>
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={sim.scenarios}>
                    <CartesianGrid stroke="var(--c-border)" strokeDasharray="3 3" />
                    <XAxis dataKey="projects_per_month" {...axis} />
                    <YAxis {...axis} />
                    <Tooltip contentStyle={tip} />
                    <Legend wrapperStyle={{ fontSize: 10 }} />
                    <Bar dataKey="revenue_usd" name="Revenue" fill="var(--c-accent)" radius={[3, 3, 0, 0]} />
                    <Bar dataKey="ai_cost_usd" name="AI cost" fill="var(--c-ember)" radius={[3, 3, 0, 0]} />
                    <Bar dataKey="gross_margin_usd" name="Gross margin" fill="var(--c-green)" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
          {sim && (
            <div className="rounded-lg border border-[var(--c-border)] bg-[var(--c-surface)] overflow-x-auto" data-testid="tier-table">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-[var(--c-border)]">
                    {["Plan", "Seats", "USD/seat", "Platform fee", "Projects/mo", "Revenue", "AI cost", "Margin %", "Breakeven"].map((h) => (
                      <th key={h} className="text-left px-3 py-2 text-[9px] font-mono uppercase tracking-widest text-[var(--c-muted)]">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sim.tiers.map((t) => (
                    <tr key={t.tier} className="border-b border-[var(--c-border)]/60 last:border-0 hover:bg-[var(--c-surface-2)] transition-colors duration-150"
                      data-testid={`tier-row-${t.tier}`}>
                      <td className="px-3 py-2 text-[var(--c-text)]">{t.tier_name}</td>
                      <td className="px-3 py-2 font-mono text-[var(--c-muted)]">{t.seats}</td>
                      <td className="px-3 py-2 font-mono text-[var(--c-muted)]">{usd(t.price_per_seat_usd)}</td>
                      <td className="px-3 py-2 font-mono text-[var(--c-muted)]">{usd(t.platform_fee_usd)}</td>
                      <td className="px-3 py-2 font-mono text-[var(--c-muted)]">{t.projects_per_month}</td>
                      <td className="px-3 py-2 font-mono text-[var(--c-text)]">{usd(t.revenue_usd)}</td>
                      <td className="px-3 py-2 font-mono text-[var(--c-ember)]">{usd(t.ai_cost_usd)}</td>
                      <td className="px-3 py-2 font-mono text-[var(--c-green)]">{t.gross_margin_pct}%</td>
                      <td className="px-3 py-2 font-mono text-[var(--c-muted)]">{t.breakeven_projects}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {user?.is_superadmin && lic && (
        <div className="mt-6 rounded-lg border border-[var(--c-ember)]/40 bg-[var(--c-surface)] p-4" data-testid="plan-editor">
          <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-[var(--c-ember)] mb-3">
            <CurrencyDollar size={13} /> Superadmin — plan for {lic.org_name}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <div className="space-y-1.5">
              <Label className="text-[var(--c-muted)] text-xs">Tier</Label>
              <Select value={plan.tier} onValueChange={(v) => setPlan({ ...plan, tier: v })}>
                <SelectTrigger data-testid="plan-tier" className="bg-[var(--c-bg)] border-[var(--c-border)] text-[var(--c-text)] h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[var(--c-surface)] border-[var(--c-border)]">
                  {tiers.map((t) => <SelectItem key={t.key} value={t.key} className="text-[var(--c-text)]">{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {[["seats", "Seats"], ["projects_per_month", "Projects / mo"], ["ai_ops_per_month", "AI ops / mo"]].map(([k, l]) => (
              <div key={k} className="space-y-1.5">
                <Label className="text-[var(--c-muted)] text-xs">{l}</Label>
                <Input data-testid={`plan-${k}`} value={plan[k]} onChange={(e) => setPlan({ ...plan, [k]: Number(e.target.value) || 0 })}
                  className="bg-[var(--c-bg)] border-[var(--c-border)] text-[var(--c-text)] font-mono h-8 text-xs" />
              </div>
            ))}
            <div className="space-y-1.5">
              <Label className="text-[var(--c-muted)] text-xs">Enforce limits</Label>
              <div className="h-8 flex items-center">
                <Switch data-testid="plan-enforced" checked={plan.enforced} onCheckedChange={(v) => setPlan({ ...plan, enforced: v })} />
              </div>
            </div>
          </div>
          <Button size="sm" data-testid="plan-save" onClick={savePlan} disabled={busy}
            className="mt-3 bg-[var(--c-accent)] hover:bg-[var(--c-accent-hover)] text-white rounded-sm h-8 text-xs">Save plan</Button>
        </div>
      )}
    </div>
  );
}
